import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import ColumnSettings from "@/components/ColumnSettings";
import SavedViews from "@/components/SavedViews";
import ColumnHeader, { sortRows } from "@/components/table/ColumnHeader";
import { useColumnPrefs, cellStyle } from "@/hooks/useColumnPrefs";
import { Settings2 } from "lucide-react";

// Presentational, client-data table with the SAME column-preference framework as
// DataTable (show/hide, reorder, resize, pin, reset — persisted per user/company/table).
// Use for bespoke pages that fetch their own rows and need totals/grouping. It never
// fetches data or changes any contract: just pass `tableId`, `columns`, and `rows`.
//
// columns: [{ key, label, align, mono, strong, truncate, render?(value,row),
//             mandatory?, defaultHidden?, pinnable?, defaultWidth?, defaultPin? }]
export default function PrefTable({
  tableId, columns, rows, rowKey, onRowClick, emptyText = "No records found.",
  className = "", testid, rowTestId, footer,
}) {
  const [open, setOpen] = useState(false);
  const { prefs, displayed, sticky, apply, setWidth, setSort, applyAll, reset } = useColumnPrefs(tableId, columns);
  const sort = prefs.sort;
  const onSort = (key, dir) => setSort(key, dir);
  const sortedRows = useMemo(() => sortRows(rows, sort, columns), [rows, sort, columns]);
  const keyOf = (row, i) => (rowKey ? rowKey(row) : (row.id ?? row.legacy_id ?? i));
  const pinBody = (s) => (s ? `bg-white ${s.last ? "shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)]" : ""} ${s.first ? "shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.12)]" : ""}` : "");

  return (
    <div className={className} data-testid={testid}>
      <div className="flex justify-end gap-2 mb-2">
        <SavedViews tableId={tableId} prefs={prefs} filters={{}} onApplyView={(p) => applyAll(p)} />
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setOpen(true)}
          data-testid={`${tableId}-column-settings-button`} title="Table settings — columns, order, pin, resize">
          <Settings2 className="w-4 h-4 text-slate-600" />
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" data-testid={`${tableId}-table`}>
          <thead>
            <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 border-b border-slate-200">
              {displayed.map((c) => (
                <ColumnHeader key={c.key} col={c} style={cellStyle(c, prefs.widths, sticky)} sticky={sticky[c.key]} onResize={setWidth} sort={sort} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && <tr key="pt-empty-cols"><td className="px-4 py-12 text-center text-slate-400 text-sm">No columns selected.</td></tr>}
            {displayed.length > 0 && (!sortedRows || sortedRows.length === 0) && (
              <tr key="pt-empty-rows"><td colSpan={displayed.length} className="px-4 py-12 text-center text-slate-400 text-sm">{emptyText}</td></tr>
            )}
            {displayed.length > 0 && (sortedRows || []).map((row, i) => (
              <tr key={keyOf(row, i)} onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-slate-100 hover:bg-slate-50/70 transition-colors ${onRowClick ? "cursor-pointer" : ""}`}
                data-testid={rowTestId ? rowTestId(row) : undefined}>
                {displayed.map((c) => (
                  <td key={c.key} style={cellStyle(c, prefs.widths, sticky)}
                    className={["px-6 py-4", c.align === "right" ? "text-right" : "", c.mono ? "font-mono text-xs text-slate-500" : "", c.strong ? "font-semibold text-slate-900" : "text-slate-600", c.truncate ? "max-w-[240px] truncate" : "whitespace-nowrap", pinBody(sticky[c.key])].join(" ")}
                    title={c.truncate && typeof row[c.key] === "string" ? row[c.key] : undefined}>
                    {c.render ? c.render(row[c.key], row) : (row[c.key] ?? <span className="text-slate-300">—</span>)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer}
        </table>
      </div>
      <ColumnSettings open={open} onOpenChange={setOpen} columns={columns}
        order={prefs.order} visibleKeys={prefs.visible} pinned={prefs.pinned} onApply={apply} onReset={reset} />
    </div>
  );
}
